import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, takeUntil, withLatestFrom } from 'rxjs';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { Store } from '@ngrx/store';
import { Customization } from '../../../shared/models/customization';
import { PrepareActions } from '@app/client/uploader/store/actions';
import { Logo } from '@app/shared/models/logo';
import { Background } from '@app/shared/models/background';

@Injectable({
    providedIn: 'root'
})
export class ResolveCustomizationService {

    constructor(
        private readonly store$: Store<fromUploader.State>,
    ) { }

    resolve(): Observable<{ customization: Customization, backgrounds: Background[], logos: Logo[] }> {

        const resolvedCustomization$ = new ReplaySubject<{ customization: Customization, backgrounds: Background[], logos: Logo[] }>();
        this.store$.pipe(
            withLatestFrom(
                this.store$.select(fromUploader.getCustomization),
                this.store$.select(fromUploader.getCustomizationLoading),
                this.store$.select(fromUploader.getCustomizationLoaded),
                this.store$.select(fromUploader.getCustomizationError),
                this.store$.select(fromUploader.getCustomizationBackgrounds),
                this.store$.select(fromUploader.getCustomizationBackgroundsLoading),
                this.store$.select(fromUploader.getCustomizationBackgroundsLoaded),
                this.store$.select(fromUploader.getCustomizationLogos),
                this.store$.select(fromUploader.getCustomizationLogosLoading),
                this.store$.select(fromUploader.getCustomizationLogosLoaded),
            ),
            // Tear the store subscription down as soon as we emit — matching the sibling
            // resolve-external-promotion.ts. Without it this providedIn:'root' service leaks a
            // permanent subscription per resolve() call, and (since the fix added two more emit
            // branches) fires a no-op .next() into an unread infinite-buffer ReplaySubject on every
            // subsequent store emission, forever.
            takeUntil(resolvedCustomization$),
        ).subscribe(([store,
            customization, customizationLoading, customizationLoaded, customizationError,
            backgrounds, backgroundsLoading, backgroundsLoaded,
            logos, logosLoading, logosLoaded,
        ]) => {
            if (customizationError) {
                resolvedCustomization$.next({ customization, backgrounds, logos });
            } else if (!customizationLoading && !customizationLoaded) {
                this.store$.dispatch(PrepareActions.loadCustomization());
            } else if (customizationLoaded && !customization) {
                // No managed/team customization configured for this domain — a valid, common
                // outcome, not an error. There is nothing to enrich with backgrounds/logos (the
                // branches below never dispatch without a customization), so resolve immediately
                // instead of leaving the ReplaySubject waiting forever.
                resolvedCustomization$.next({ customization, backgrounds, logos });
            } else if (customization && !backgroundsLoading && !backgroundsLoaded) {
                this.store$.dispatch(PrepareActions.loadBackgrounds());
            } else if (customization && !logosLoading && !logosLoaded) {
                this.store$.dispatch(PrepareActions.loadLogos());
            } else if (customization && backgroundsLoaded && logosLoaded) {
                // Backgrounds and logos have both settled — even if either list came back empty
                // (no custom assets configured), that is a resolved state, not a still-loading one.
                resolvedCustomization$.next({ customization, backgrounds, logos });
            }
        });

        return resolvedCustomization$.asObservable();
    }

}